import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  Skeleton,
} from "@mui/material";
import { Edit, Delete, Visibility, FilterList, Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import { useStaffLayout } from "../../Components/Context/CandidateLayoutContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const ListRole = ({ isAdmin }) => {
  const navigate = useNavigate();

  const { privilegeData } = useStaffLayout();

  function hasRolesAddprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName.toLowerCase().includes("roles");

      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "add" && priv.assigned == true
      );
    });
  }

  function hasRolesEditprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isJob = subModule.subModuleName.toLowerCase().includes("roles");
      if (!isJob) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned == true
      );
    });
  }

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [loader, setLoader] = useState(true);
  const [data, setData] = useState([]);

  const getData = () => {
    axiosAPI
      ?.get(`/UserRole/list/Active`)
      .then((res) => {
        setLoader(false);
        if (res?.data?.data) {
          setData(res?.data?.data);
        }
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const navigatetoAdd = () => navigate("/Roles/Add");
  const navigatetoEdit = (id) => {
    const encrypted = encodeURIComponent(encryptId(id));
    navigate(`/Roles/Edit/${encrypted}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Role Management
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {hasRolesAddprivilege(privilegeData) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ background: "#E75E1F" }}
              onClick={navigatetoAdd}
              disabled={!hasRolesAddprivilege(privilegeData)}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width:"50px" }}>#</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                Status
              </StyledTableCell>
              {hasRolesEditprivilege(privilegeData) && (
                <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                  Actions
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loader
              ? Array.from({ length: 7 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 4 }).map((_, index) => (
                      <StyledTableCell key={index}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={40}
                        />
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))
              : // Render actual data when available
                data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((parent, index) => (
                    <StyledTableRow
                      key={parent.id}
                      sx={{ border: "2px solid transparent" }}
                    >
                      <StyledTableCell
                        sx={{ fontSize: { xs: "12px", sm: "14px" } }}
                      >
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "12px", sm: "14px" },
                            fontFamily: "Geist, sans-serif",
                          }}
                        >
                          {parent?.RL_Name ? parent?.RL_Name : "-"}
                        </Typography>
                      </StyledTableCell>

                      {/* Status Column - Rightmost */}
                      <StyledTableCell
                        sx={{ width: "150px", textAlign: "right" }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Chip
                            label={
                              parent.RL_Status == "1" ? "Active" : "Inactive"
                            }
                            color={
                              parent.RL_Status == "1" ? "success" : "error"
                            }
                            sx={{
                              fontSize: { xs: "10px", sm: "12px" },
                              width: "80px",
                              fontFamily: "Geist, sans-serif",
                              fontWeight: "600",
                            }}
                          />
                        </Box>
                      </StyledTableCell>

                      {/* Actions Column - Rightmost */}
                      {hasRolesEditprivilege(privilegeData) && (
                        <StyledTableCell
                          sx={{ width: "150px", textAlign: "center" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "5px",
                            }}
                          >
                            <IconButton
                              color="warning"
                              onClick={() => {
                                navigatetoEdit(parent?.RL_Id);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Box>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                  ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default ListRole;
